import { format } from "date-fns/format";
import { isSameYear } from "date-fns/isSameYear";
import { isToday } from "date-fns/isToday";
import { isTomorrow } from "date-fns/isTomorrow";
import { isYesterday } from "date-fns/isYesterday";

export function formatDateShort(date: Date) {
  if (isToday(date)) {
    return `Today`;
  }

  if (isYesterday(date)) {
    return `Yesterday`;
  }

  if (isTomorrow(date)) {
    return `Tomorrow`;
  }

  if (isSameYear(date, new Date())) {
    return format(date, "MMM d");
  }

  return format(date, "P");
}

export function dateForHumans(date: Date): string {
  const d = new Date(date);
  return new Intl.DateTimeFormat("en", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(d);
}

export function dateWithoutYearForHumans(date: Date): string {
  const d = new Date(date);
  return new Intl.DateTimeFormat("en", {
    day: "numeric",
    month: "long",
  }).format(d);
}

export function yearForHumans(date: Date): string {
  const d = new Date(date);
  return new Intl.DateTimeFormat("en", { year: "numeric" }).format(d);
}

export function dateFromNowForHumans(date: Date): string {
  const d = new Date(date);
  const now = new Date();
  // @ts-expect-error date error
  const diffInSeconds = Math.floor((now - d) / 1000);

  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

  const units = [
    { unit: "year", seconds: 31536000 },
    { unit: "month", seconds: 2592000 },
    { unit: "week", seconds: 604800 },
    { unit: "day", seconds: 86400 },
    { unit: "hour", seconds: 3600 },
    { unit: "minute", seconds: 60 },
    { unit: "second", seconds: 1 },
  ];

  for (const { unit, seconds } of units) {
    if (Math.abs(diffInSeconds) >= seconds) {
      const value = Math.floor(diffInSeconds / seconds);
      // @ts-expect-error relative time
      return rtf.format(-value, unit);
    }
  }

  return "";
}

export function getStartOfToday(targetDate: Date): Date {
  const today = new Date(targetDate);
  today.setHours(0, 0, 0, 0);
  return today;
}

export function getEndOfToday(): Date {
  const today = new Date();
  const endOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    23,
    59,
    59,
    999,
  );
  return endOfToday;
}

export function isDateGreaterThanEndOfToday(dateToCompare: Date): boolean {
  const endOfToday = getEndOfToday();

  return dateToCompare.getTime() > endOfToday.getTime();
}

export function isDateInArray(dateArray: Date[], targetDate?: Date) {
  if (targetDate) {
    for (const dateItem of dateArray) {
      if (dateItem.getTime() === targetDate.getTime()) {
        return true;
      }
    }
  }
  return false;
}

export function getCurrentMonth() {
  const today = new Date();
  const currentMonth = new Date(today.getFullYear(), today.getMonth());
  return currentMonth.getMonth();
}

export function getPreviousMonth() {
  const today = new Date();
  const previousMonth = new Date(today.getFullYear(), today.getMonth() - 1);
  return previousMonth.getMonth();
}

export function formatDateWithDayOfWeek(input: string | number): string {
  const date = new Date(input);
  const formattedDate = date.toLocaleDateString('en-US', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  // Add the ordinal suffix (e.g., 1st, 2nd, 3rd, 4th)
  const day = date.getDate();
  const ordinalSuffix = getOrdinalSuffix(day);
  return formattedDate.replace(/\d+/, `${day}${ordinalSuffix}`);
}

function getOrdinalSuffix(day: number): string {
  if (day >= 11 && day <= 13) {
    return 'th';
  }
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}